import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { supabase } from '../config/supabase';

interface UnreadMessagesContextType {
  hasUnreadMessages: boolean;
  unreadCounts: Record<string, number>;
  setHasUnreadMessages: (value: boolean) => void;
  refreshUnreadStatus: () => Promise<void>;
  markMessagesAsRead: (senderId: string) => Promise<void>;
}

const UnreadMessagesContext = createContext<UnreadMessagesContextType | undefined>(undefined);

export const UnreadMessagesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [hasUnreadMessages, setHasUnreadMessages] = useState(false);
  const [unreadCounts, setUnreadCounts] = useState<Record<string, number>>({});
  const { user } = useAuth();

  const refreshUnreadStatus = async () => {
    if (!user) return;

    try {
      // Obtener todos los mensajes no leídos agrupados por sender_id
      const { data, error } = await supabase
        .from('messages')
        .select('sender_id')
        .eq('receiver_id', user.id)
        .eq('read', false);

      if (error) throw error;

      // Contar mensajes por remitente
      const counts: Record<string, number> = {};
      data.forEach(message => {
        counts[message.sender_id] = (counts[message.sender_id] || 0) + 1;
      });

      setUnreadCounts(counts);
      setHasUnreadMessages(data.length > 0);
    } catch (error) {
      console.error('Error al verificar mensajes no leídos:', error);
    }
  };

  const markMessagesAsRead = async (senderId: string) => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from('messages')
        .update({ read: true })
        .eq('receiver_id', user.id)
        .eq('sender_id', senderId)
        .eq('read', false);

      if (error) throw error;

      // Actualizar el estado local
      setUnreadCounts(prev => {
        const newCounts = { ...prev };
        delete newCounts[senderId];
        return newCounts;
      });

      // Verificar si aún hay mensajes sin leer de otros usuarios
      const remainingUnread = Object.values(unreadCounts).reduce((a, b) => a + b, 0) - (unreadCounts[senderId] || 0);
      setHasUnreadMessages(remainingUnread > 0);
    } catch (error) {
      console.error('Error al marcar mensajes como leídos:', error);
    }
  };

  useEffect(() => {
    if (user) {
      refreshUnreadStatus();

      // Suscribirse a cambios en mensajes
      const channel = supabase
        .channel('unread-messages')
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'messages',
            filter: `receiver_id=eq.${user.id}`
          },
          () => {
            refreshUnreadStatus();
          }
        )
        .subscribe();

      return () => {
        supabase.removeChannel(channel);
      };
    }
  }, [user]);

  return (
    <UnreadMessagesContext.Provider
      value={{
        hasUnreadMessages,
        unreadCounts,
        setHasUnreadMessages,
        refreshUnreadStatus,
        markMessagesAsRead
      }}
    >
      {children}
    </UnreadMessagesContext.Provider>
  );
};

export const useUnreadMessages = () => {
  const context = useContext(UnreadMessagesContext);
  if (context === undefined) {
    throw new Error('useUnreadMessages debe ser usado dentro de un UnreadMessagesProvider');
  }
  return context;
};
